'use client';

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { buildAttractionUrl, type AttractionSummary } from '../config';
import { useAttractionMoney } from './Money';
import { PLACEHOLDER_IMAGE } from '../_data/mapper';
import { ArrowRightIcon } from './Icons';
import { StarRating } from './StarRating';

/**
 * The card's photo, with the same no-image handling the Shows card uses.
 *
 * Two distinct cases both have to land on the placeholder:
 *  - the product carries NO photo, which `toSummary` already resolves to
 *    `PLACEHOLDER_IMAGE`;
 *  - the product carries a photo URL that FAILS to load. Supplier URLs rot,
 *    and without an `onError` the browser drew its own broken-image glyph.
 *
 * The placeholder is then fitted differently from a real photo — see
 * `.atr-rcard__media img.is-placeholder`. It is a 600x400 graphic with its own
 * background, so cropping it to fill a 16:10 box (as `object-fit: cover` does)
 * cut into the artwork.
 */
function CardImage({ src }: { src: string }) {
  const [current, setCurrent] = useState(src || PLACEHOLDER_IMAGE);

  // A re-rendered card (filter change, appended page) must follow its new
  // product rather than keep the previous one's resolved src.
  useEffect(() => setCurrent(src || PLACEHOLDER_IMAGE), [src]);

  const isPlaceholder = current === PLACEHOLDER_IMAGE;

  return (
    <img
      src={current}
      alt=""
      loading="lazy"
      className={isPlaceholder ? 'is-placeholder' : undefined}
      // Guarded, so a placeholder that itself 404s cannot loop.
      onError={() => {
        if (current !== PLACEHOLDER_IMAGE) setCurrent(PLACEHOLDER_IMAGE);
      }}
    />
  );
}

interface AttractionCardProps {
  attraction: AttractionSummary;
  /** `grid` (default) is the 3-up card; `list` is the wide row variant. */
  view?: 'grid' | 'list';
}

/**
 * Result card from `home-page.png` — a white ticket-shaped card (concave
 * corners via `.atr-ticket`) with a duration badge over the image.
 *
 * `'use client'` for the image's `onError` fallback only. It is rendered
 * exclusively by `AttractionsList`, which is already a client component, so
 * this adds nothing to the bundle that was not there before.
 */
/**
 * Every link into the detail page opens a NEW TAB, so the listing — with its
 * filters, sort and infinite-scroll position — survives the visit. All three
 * (image, title, button) share it; a card where only the button opened a tab
 * behaved differently depending on where the user happened to click.
 */
const NEW_TAB = { target: '_blank', rel: 'noopener noreferrer' } as const;

export function AttractionCard({ attraction, view = 'grid' }: AttractionCardProps) {
  const href = buildAttractionUrl(attraction);
  const isRow = view === 'list';
  // Shared navbar currency mechanism — same flow Hotels/Sports/Concerts cards use.
  const { money } = useAttractionMoney();

  return (
    <article className={`atr-rcard atr-ticket${isRow ? ' atr-rcard--row' : ''}`}>
      <Link href={href} className="atr-rcard__media" aria-label={attraction.title} {...NEW_TAB}>
        <CardImage src={attraction.image} />
        {/* No duration from the supplier → no badge. Rendering it regardless
            put a bare clock icon over the image with nothing beside it. */}
        {/* {attraction.durationLabel && (
          <span className="atr-rcard__badge">
           />
            {attraction.durationLabel}
          </span>
        )} */}
      </Link>

      <div className="atr-rcard__body">
        <Link href={href} className="atr-rcard__title" {...NEW_TAB}>
          {attraction.title}
        </Link>

        <StarRating rating={attraction.rating} reviewCount={attraction.reviewCount} />

        {attraction.summary && <p className="atr-rcard__desc">{attraction.summary}</p>}

        {/* `price: null` is a documented Bridgify pattern, not a data gap: those
            suppliers do not quote until ticket quantities are submitted, and
            ~40% of the catalogue arrives that way. Printing the mapper's 0
            fallback as "From £0" advertised a price the supplier never gave us —
            the same gate the hotels `ShowsNearby` card uses. */}
        {attraction.fromPrice > 0 && (
          <p className="atr-rcard__price">
            From {money(attraction.fromPrice, attraction.currency)}
          </p>
        )}

        <div className="atr-rcard__foot">
          <Link href={href} className="atr-btn atr-btn-primary" {...NEW_TAB}>
            More Info
            <ArrowRightIcon />
          </Link>
        </div>
      </div>
    </article>
  );
}
